import { InputMask } from '@react-input/mask';
import { HandPlatter, SquareCheck } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useContext, useEffect, useRef, useState } from 'react';
import { FaPlus } from "react-icons/fa6";
import { MdOutlineDeliveryDining } from "react-icons/md";
import BoxAlert from '../../components/boxAlert';
import Loader from '../../components/loader';
import ClientDataContext from '../../contexts/clientContext';
import CompanyContext from '../../contexts/companyContext';
import LocationContext from '../../contexts/locationContext';
import '../../css/scrollbar.css';
import { useAppRouter } from '../../hooks/useAppRouter';
import useStorage from '../../hooks/useStorage';
import { formatValue } from '../../scripts/formatValue';
import { validateClientName } from '../../scripts/validateClientName';
import { RenderProducts } from './components/renderProducts';
import styles from './css/cart.module.css';
import { useOrderFlow } from './hooks/useOrderFlow';
import { changeAmount } from './services/changeAmount';
import { deleteProduct } from './services/deleteProduct';
import { BsTrash } from 'react-icons/bs';
import { ORDERS_BLOCKED_MESSAGE } from '../../scripts/canPlaceOrders';



function Cart() {
    const storage = useStorage();
    const navigate = useAppRouter();
    const { companyData } = useContext(CompanyContext);
    const { location } = useContext(LocationContext);
    const { setClientData } = useContext(ClientDataContext);

    const cart = useRef(null);
    const clientArray = useRef(storage.get('CLIENT_DATA'));

    const [products, setProducts] = useState([]);
    const [totalPrice, setTotalPrice] = useState(0);
    const [loading, setLoading] = useState(true);

    // ALERTAS DE GERENCIAMENTO DO CARRINHO JÁ MONTADO (produtos adicionados)
    const [clearCartAlert, setClearCartAlert] = useState(false);
    const [deleteProductAlert, setDeleteProductAlert] = useState({ show: false, index: null });


    // CARREGA O CARRINHO SALVO NO DISPOSITIVO
    useEffect(() => {
        cart.current = storage.get('CART');

        if (cart.current?.products?.length > 0) {
            setProducts(cart.current.products);
            setTotalPrice(cart.current.cart_price);
        }

        setLoading(false);
    }, []);


    // MUDA A QUANTIDADE DE UM PRODUTO NO CARRINHO ===============================>
    const changeAmountHandler = ({ type, index }) => {
        const { products: nextProducts, cart: nextCart } = changeAmount({ type, index, cart: cart.current, products });

        cart.current = nextCart;
        setProducts(nextProducts);
        setTotalPrice(nextCart.cart_price);

        storage.save('CART', nextCart);
    };


    // REMOVE UM PRODUTO DO CARRINHO ==============================>
    const handleDeleteProduct = (index) => {
        const nextCart = deleteProduct({ index, cart: cart.current });

        cart.current = nextCart;
        setProducts(nextCart.products);
        setTotalPrice(nextCart.cart_price);

        storage.save('CART', nextCart);

        setDeleteProductAlert({ show: false, index: null });
    };


    // ESVAZIA O CARRINHO
    const clearCart = () => {
        setClearCartAlert(false);

        cart.current = { ...cart.current, products: [], cart_price: 0 };

        setProducts([]);
        setTotalPrice(0);

        storage.save('CART', cart.current);
    };


    // FLUXO DE IDENTIFICAÇÃO DO CLIENTE E FINALIZAÇÃO DO PEDIDO (ver hooks/useOrderFlow.js)
    const {
        requireDocumentAlert, setRequireDocumentAlert,
        clientDocument, setClientDocument,
        registerAlert, setRegisterAlert,
        clientNameAlert, setClientNameAlert,
        clientName, setClientName,
        olderAlert, setOlderAlert,
        orderTypeAlert, setOrderTypeAlert,
        orderTypeValue, setOrderTypeValue,
        clientPhoneAlert, setClientPhoneAlert,
        clientPhone, setClientPhone,
        peopleCount, setPeopleCount,
        peopleCountAlert, setPeopleCountAlert,
        peopleCountAsked,
        pendingAnalyzeClient,
        validateInfoAlert, setValidateInfoAlert,
        validateInformation,
        warnAlert, setWarnAlert,
        findClientData,
        analyzeData,
        makeOrder,
    } = useOrderFlow({
        storage,
        navigate,
        companyData,
        location,
        clientArray,
        cart,
        products,
        setClientData,
        setLoading,
        handleDeleteProduct,
        clearCart,
    });




    // SCREEN ===============================================================>

    return (
        <div>
            {loading ? (
                <Loader />
            ) : (
                <div>

                    <div className={styles.screen}>
                        {/* <======================= HEADER =======================> */}
                        <header className={styles.headerCart} style={{ backgroundColor: companyData.background_color }}>
                            <div className={styles.headerCartTextArea}>
                                <h1 className={styles.titleCart} style={{ color: companyData.text_color }}>Seu pedido</h1>
                                <p className={styles.subtitleCart} style={{ color: companyData.text_color }}>
                                    {products && products.length > 0 ? 'Revise os itens antes de finalizar' : 'Monte seu pedido pelo cardápio'}
                                </p>
                            </div>
                        </header>

                        <div id={styles.container}>
                            {/* <======================= PRODUCTS RENDER =======================> */}
                            <div className={styles.productsArea}>

                                {companyData.orders_blocked ? (
                                    <div className={styles.emptyCartArea}>
                                        <h2 className={styles.emptyCartTitle}>Pedidos indisponíveis</h2>
                                        <p className={styles.emptyCartText}>{ORDERS_BLOCKED_MESSAGE}</p>

                                        <button className={styles.addProductsButton} style={{backgroundColor: companyData.background_color}} onClick={() => navigate('menu')}>
                                            <p className={styles.addProductsButtonText} style={{color: companyData.text_color}}>Ver cardápio</p>
                                        </button>
                                    </div>

                                ) : products && products.length > 0 ? (
                                    <>
                                        <div className={styles.topList}>
                                            <h1 className={styles.titleList}>Produtos:</h1>

                                            {products && products.length > 0 ? (
                                                <button
                                                    className={styles.excludeListBtn}
                                                    onClick={() => setClearCartAlert(true)}
                                                >
                                                    <BsTrash size={13} color="#fff" />
                                                    <span>Apagar tudo</span>
                                                </button>
                                            ) : null}

                                        </div>

                                        {products.map((item, index) => (
                                            <RenderProducts
                                                item={item}
                                                index={index}
                                                deleteAlert={setDeleteProductAlert}
                                                changeAmount={changeAmountHandler}
                                                companyData={companyData}
                                            />
                                        ))}

                                        <div className={styles.informationsArea}>
                                            <span className={styles.totalValueArea}>
                                                <b>Total:</b> {formatValue(totalPrice)}
                                            </span>
                                            <button className={styles.orderButton} style={{ backgroundColor: companyData.background_color }} onClick={() => {
                                                if(companyData.type !== 'delivery'){
                                                    makeOrder();

                                                } else {
                                                    // setOrderTypeAlert(true); // Para implementação do módulo de pedidos para retirada
                                                    navigate('register/delivery');
                                                }
                                            }}>
                                                <SquareCheck color={companyData.text_color} size={15}/>
                                                <span style={{ color: companyData.text_color, fontWeight: 600 }}>Concluir</span>
                                            </button>
                                        </div>
                                    </>

                                ) : (
                                    <div className={styles.emptyCartArea}>

                                        <h2 className={styles.emptyCartTitle}>Seu carrinho está vazio</h2>
                                        <p className={styles.emptyCartText}>Adicione produtos do cardápio para montar seu pedido.</p>

                                        <button className={styles.addProductsButton} style={{backgroundColor: companyData.background_color}} onClick={() => navigate('menu')}>
                                            <FaPlus size={18} color={companyData.text_color} />
                                            <p className={styles.addProductsButtonText} style={{color: companyData.text_color}}>Adicionar produtos</p>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {validateInfoAlert && validateInformation && (
                        <div className={styles.valInfoAlertArea}>
                            <div id={styles.infoAlertOverflow}/>

                            <div className={styles.valInfoBox}>
                                <h3 className={styles.infoBoxTitle}>Validação de dados</h3>

                                <p style={{marginTop: 20, textAlign: 'center'}}>Para gerarmos sua comanda com seu CPF, mostre o código abaixo para um garçon para que ele possa liberar sua comanda e realizar seus pedidos!</p>

                                <div style={{width: '80%', paddingTop: 10, paddingBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 20, borderRadius: 10, backgroundColor: '#d3d3d3'}}>
                                    <p>Documento: {clientDocument}</p>
                                </div>

                                <QRCodeSVG value={validateInformation.code} size={120} style={{marginTop: 30}} />

                                <p style={{marginTop: 15, fontSize: 12, textAlign: 'center'}}>Código: {validateInformation.code}</p>

                                <button className={styles.validCommandButton} style={{backgroundColor: companyData.background_color}} onClick={() => {
                                    setValidateInfoAlert(false);
                                    makeOrder();
                                }}>
                                    <p style={{color: companyData.text_color}}>Prosseguir</p>
                                </button>
                            </div>
                        </div>
                    )}

                    {/* <====================== REQUEST DATA ALERT  ======================> */}
                    <BoxAlert
                        show={requireDocumentAlert}
                        title={'Insira seus dados'}
                        text={<p>Vamos encontrar os dados da sua comanda</p>}
                        showCloseButton={true}
                        input={
                            <InputMask
                                className={styles.inputRequestData}
                                style={{borderColor: companyData.background_color}}
                                name="document"
                                type="text"
                                value={clientDocument}
                                onChange={({ target: { value } }) => setClientDocument(value)}
                                mask="___.___.___-__"
                                replacement={{ _: /\d/ }}
                                placeholder="000.000.000-00"
                            />
                        }
                        labelInput={'CPF:'}
                        confirmButtonText={'Prosseguir'}
                        confirmFunction={async () => await findClientData()}
                        onClose={() => setRequireDocumentAlert(false)}
                    />


                    {/* <====================== REGISTER ALERT  ======================>
                        (PARA O CASO DO CLIENTE NÃO TER UMA CONTRA PRÉ-REGISTRADA)
                    */}
                    <BoxAlert
                        show={registerAlert}
                        title={'Não encontramos você!'}
                        text={
                            <p className={styles.textAlertBox}>
                                Infelizmente parece que você ainda não é registrado em nosso sistema.
                                <br />
                                <span style={{ color: companyData.background_color }}>Registre-se agora &nbsp;</span>e tenha acesso à<span style={{ color: companyData.background_color }}>&nbsp; diversos benefícios!</span>
                            </p>
                        }
                        showButtons={true}
                        cancelFunction={() => {
                            setClientNameAlert(true);
                            setRegisterAlert(false);
                        }}
                        cancelButtonText={'Não registrar'}
                        confirmFunction={() => {
                            sessionStorage.setItem('CLIENT_DOCUMENT', JSON.stringify(clientDocument));
                            setRegisterAlert(false);
                            navigate('register');
                        }}
                        confirmButtonText={'Prosseguir'}
                        onClose={() => setRegisterAlert(false)}
                    />

                    {/* <====================== NAME REQUIRE ALERT  ======================> */}

                    <BoxAlert
                        show={clientNameAlert}
                        title={'Nome do cliente'}
                        text={<p className={styles.textAlertBox}>Insira seu nome para que possamos te encontrar e entregar seus pedidos sem erro!</p>}
                        input={
                            <input
                                type="text"
                                className={styles.inputRequestData}
                                style={{borderColor: companyData.background_color}}
                                value={clientName}
                                onChange={({ target: { value } }) => setClientName(value)}
                                placeholder={'Insira seu nome...'}
                            />
                        }
                        labelInput={'Nome completo:'}
                        confirmButtonText={'Continuar'}
                        onClose={() => {
                            const validName = validateClientName(clientName);

                            if(validName){
                                if(orderTypeValue === 'takeaway'){
                                    setClientPhoneAlert(true);
                                    setClientNameAlert(false);

                                } else {
                                    clientArray.current.name = clientName;
                                    setOlderAlert(true);
                                    setClientNameAlert(false);
                                }

                            } else {
                                setWarnAlert({show: true, title: 'Alerta', text: 'Insira um nome antes de continuar!'});
                            }
                        }}
                    />



                    <BoxAlert
                        show={clientPhoneAlert}
                        title={'Telefone do cliente'}
                        text={<p className={styles.textAlertBox}>Insira seu telefone para que possamos te contatar caso necessário!</p>}
                        input={
                            <input
                                type="text"
                                className={styles.inputRequestData}
                                style={{borderColor: companyData.background_color}}
                                value={clientPhone}
                                onChange={({ target: { value } }) => setClientPhone(value)}
                                placeholder={'(11) 99999-9999'}
                            />
                        }
                        labelInput={'Telefone:'}
                        confirmButtonText={'Continuar'}
                        onClose={() => {
                            const unformattedPhone = clientPhone.replace('(', '')
                                .replace(')', '')
                                .replace('-', '')
                                .replaceAll(' ', '');

                            if(unformattedPhone.length === 11){
                                makeOrder();

                            } else {
                                setWarnAlert({show: true, title: 'Alerta', text: 'Insira um nome antes de continuar!'});
                            }
                        }}
                    />

                    {/* <====================== AGE REQUIRE ALERT  ======================> */}

                    <BoxAlert
                        show={olderAlert}
                        title={'Maior de idade'}
                        text={<p className={styles.textAlertBox}>A partir daqui você concorda em nos dizer, clicando nos botões a baixo, se é ou não maior de idade.</p>}
                        showButtons={true}
                        cancelButtonText={'-18'}
                        cancelFunction={() => {
                            clientArray.current.older = false;
                            setOlderAlert(false)
                            analyzeData(clientArray.current);
                        }}
                        confirmButtonText={'+18'}
                        confirmFunction={() => {
                            clientArray.current.older = true;
                            setOlderAlert(false)
                            analyzeData(clientArray.current);
                        }}
                        onClose={() => setOlderAlert(false)}
                    />

                    {/* <====================== QTD. DE PESSOAS ALERT  ======================> */}
                    <BoxAlert
                        show={peopleCountAlert}
                        title={'Quantas pessoas?'}
                        text={<p className={styles.textAlertBox}>Quantas pessoas estão sendo atendidas por este pedido?</p>}
                        input={
                            <input
                                type="number"
                                min={1}
                                className={styles.inputRequestData}
                                style={{ borderColor: companyData.background_color }}
                                value={peopleCount}
                                onChange={({ target: { value } }) => setPeopleCount(Math.max(1, parseInt(value, 10) || 1))}
                            />
                        }
                        labelInput={'Qtd. de pessoas:'}
                        confirmButtonText={'Continuar'}
                        onClose={() => {
                            peopleCountAsked.current = true;
                            setPeopleCountAlert(false);
                            analyzeData(pendingAnalyzeClient.current);
                        }}
                    />

                    {/* <====================== CLEAR / REMOVE PRODUCTS ALERT  ======================> */}

                    <BoxAlert
                        show={clearCartAlert}
                        title={'Apagar pedido'}
                        text={<p className={styles.textAlertBox}>Tem certeza que deseja apagar o seu pedido agora?</p>}
                        showButtons={true}
                        cancelButtonText={'Cancelar'}
                        cancelFunction={() => setClearCartAlert(false)}
                        confirmButtonText={'Apagar'}
                        confirmFunction={() => clearCart()}
                        onClose={() => setClearCartAlert(false)}
                    />


                    <BoxAlert
                        show={orderTypeAlert}
                        title={'Tipo do pedido'}
                        showCloseButton
                        onClose={() => setOrderTypeAlert(false)}
                        body={
                            <div style={{width: '100%', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 10, marginTop: 30}}>
                                <button
                                    style={{width: '48%', height: 200, borderWidth: 1, borderColor: '#a3a3a3', borderRadius: 10}}
                                    onClick={() => {
                                        setOrderTypeAlert(false);
                                        navigate('register/delivery');
                                    }}
                                >
                                    <MdOutlineDeliveryDining size={45} color={companyData.background_color}/>
                                    <p style={{marginTop: 15}}>Delivery</p>
                                </button>

                                <button
                                    style={{width: '48%', height: 200, borderWidth: 1, borderColor: '#a3a3a3', borderRadius: 10}}
                                    onClick={() => {
                                        setOrderTypeValue('takeaway');
                                        setClientNameAlert(true);
                                        setOrderTypeAlert(false);
                                    }}
                                >
                                    <HandPlatter size={45} color={companyData.background_color}/>
                                    <p style={{marginTop: 15}}>Retirada</p>
                                </button>
                            </div>
                        }
                    />


                    <BoxAlert
                        show={deleteProductAlert.show}
                        title={'Apagar produto'}
                        text={<p className={styles.textAlertBox}>Tem certeza que deseja apagar o produto selecionado?</p>}
                        showButtons={true}
                        cancelButtonText={'Cancelar'}
                        cancelFunction={() => setDeleteProductAlert({show: false, index: null})}
                        confirmButtonText={'Apagar'}
                        confirmFunction={() => handleDeleteProduct(deleteProductAlert.index)}
                        onClose={() => setDeleteProductAlert({show: false, index: null})}
                    />

                    {/* <====================== WARN ALERT  ======================> */}

                    <BoxAlert
                        show={warnAlert.show}
                        title={warnAlert.title}
                        text={<p className={styles.textAlertBox}>{warnAlert.text}</p>}
                        onClose={() => setWarnAlert({ show: false, title: null, text: null })}
                    />
                </div>
            )}
        </div>
    );
}


export default Cart;
